import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import { BlankPreview } from "../../components/BlankPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import { useGetDevices } from "../../api/devices";
import DeviceSelectFilter from "./DeviceSelectFilter";
import {
  DEVICE_MODELS,
  WARRANTY_STATUS_FILTERS,
  WARRANTY_STATUS_META,
  buildActivityDetail,
  formatDate,
  getActivity,
  getWarrantyStatus,
  normalizeDevice,
} from "./deviceUtils";

const FONT = "Figtree, sans-serif";
const PAGE_SIZE = 10;

const COLUMNS = [
  { id: "deviceId", label: "DEVICE ID / SERIAL", sortable: true },
  { id: "model", label: "MODEL", sortable: true },
  { id: "purchaseDate", label: "DATE OF PURCHASE", sortable: true },
  { id: "warrantyExpiry", label: "WARRANTY EXPIRY", sortable: true },
  { id: "warrantyStatus", label: "WARRANTY STATUS", sortable: false },
  { id: "activity", label: "ACTIVITY", sortable: false },
];

const SORT_VALUES = {
  deviceId: (d) => (d.deviceId || "").toLowerCase(),
  model: (d) => (d.model || "").toLowerCase(),
  purchaseDate: (d) => (d.purchaseDate ? new Date(d.purchaseDate).getTime() : 0),
  warrantyExpiry: (d) =>
    d.warrantyExpiry ? new Date(d.warrantyExpiry).getTime() : 0,
};

function StatusChip({ status }) {
  const meta = WARRANTY_STATUS_META[status] || WARRANTY_STATUS_META.unknown;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.8}
      sx={{
        display: "inline-flex",
        px: 1.2,
        py: 0.5,
        borderRadius: "6px",
        backgroundColor: meta.bg,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: meta.color,
        }}
      />
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "12px",
          fontWeight: 700,
          color: meta.color,
          whiteSpace: "nowrap",
        }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
}

export default function Devices() {
  // Search/filter/sort are done client-side, so pull the whole list once.
  const { devices: rawDevices, isLoading } = useGetDevices({
    page: 1,
    limit: 500,
  });

  const [searchText, setSearchText] = useState("");
  const [warrantyStatus, setWarrantyStatus] = useState("all");
  const [model, setModel] = useState("all");
  const [sort, setSort] = useState({ by: "warrantyExpiry", order: "asc" });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const listRef = useRef(null);

  const devices = useMemo(
    () => (rawDevices || []).map(normalizeDevice),
    [rawDevices]
  );

  const modelFilterOptions = useMemo(() => {
    const fromData = devices.map((d) => d.model).filter(Boolean);
    const models = Array.from(new Set([...DEVICE_MODELS, ...fromData]));
    return [{ value: "all", label: "All" }, ...models.map((m) => ({ value: m, label: m }))];
  }, [devices]);

  const filtered = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    const rows = devices.filter((d) => {
      if (model !== "all" && d.model !== model) return false;
      if (warrantyStatus !== "all" && getWarrantyStatus(d) !== warrantyStatus)
        return false;
      if (!query) return true;
      return [d.deviceId, d.serialNumber, d.model]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(query));
    });

    const getValue = SORT_VALUES[sort.by];
    if (!getValue) return rows;

    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av === bv) return 0;
      const result = av > bv ? 1 : -1;
      return sort.order === "asc" ? result : -result;
    });
  }, [devices, model, warrantyStatus, searchText, sort]);

  // Start the list over whenever the visible set changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [searchText, warrantyStatus, model, sort]);

  const pageRows = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const handleScroll = (e) => {
    const target = e.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 120;
    if (reachedBottom && hasMore) {
      setVisibleCount((count) => count + PAGE_SIZE);
    }
  };

  const handleSort = (columnId) => {
    setSort((prev) =>
      prev.by === columnId
        ? { by: columnId, order: prev.order === "asc" ? "desc" : "asc" }
        : { by: columnId, order: "asc" }
    );
  };

  const isDefaultFilters =
    !searchText && warrantyStatus === "all" && model === "all";

  const handleClearFilters = () => {
    setSearchText("");
    setWarrantyStatus("all");
    setModel("all");
  };

  return (
    <Box className="main-wrappers">
      <Box className="page-wrapper" sx={{ pb: "24px", fontFamily: FONT }}>
        <Box className="content">
          <Helmet>
            <title>Devices | Spandan One</title>
          </Helmet>

          {/* Header */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "20px",
              color: "var(--app-text)",
            }}
          >
            Devices
          </Typography>

          {/* Search */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by device ID, serial number or model"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 45,
                  borderRadius: "9px",
                  backgroundColor: "var(--app-surface)",
                  fontFamily: FONT,
                  fontSize: "14px",
                  "& fieldset": { borderColor: "var(--app-border)" },
                },
              }}
            />
          </Box>

          {/* Filters */}
          <Grid
            container
            alignItems="center"
            sx={{
              mt: 2,
              gap: { xs: 1, sm: 1.5, md: "12px" },
              flexWrap: "nowrap",
              overflowX: "auto",
              whiteSpace: "nowrap",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Grid item sx={{ flex: "0 0 auto" }}>
              <DeviceSelectFilter
                label="Warranty Status"
                value={warrantyStatus}
                options={WARRANTY_STATUS_FILTERS}
                onChange={setWarrantyStatus}
                minWidth={200}
              />
            </Grid>

            <Grid item sx={{ flex: "0 0 auto" }}>
              <DeviceSelectFilter
                label="Model"
                value={model}
                options={modelFilterOptions}
                onChange={setModel}
                minWidth={170}
              />
            </Grid>

            {!isDefaultFilters && (
              <Grid item sx={{ ml: "auto", flex: "0 0 auto" }}>
                <Box
                  onClick={handleClearFilters}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "3px",
                    cursor: "pointer",
                    color: "#5751E9",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontWeight: 700,
                      fontSize: "15px",
                      textDecoration: "underline",
                    }}
                  >
                    Clear Filters
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* List header */}
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            sx={{ mt: 3, mb: 1.5 }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "17px",
                color: "var(--app-text)",
              }}
            >
              All Devices
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "15px",
                color: "var(--app-text-muted)",
              }}
            >
              ({filtered.length})
            </Typography>
          </Stack>

          {/* Table */}
          {!isLoading && filtered.length === 0 ? (
            <BlankPreview
              icon={nopreview}
              title="No devices found"
              subtitle={
                devices.length
                  ? "No device matches the selected filters. Try clearing the search or filters."
                  : "Registered devices will appear here with their warranty and usage."
              }
            />
          ) : (
            <TableContainer
              className="app-table-card"
              component={Paper}
              elevation={0}
              ref={listRef}
              onScroll={handleScroll}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                  "& .MuiTableCell-root": { borderBottom: "none" },
                }}
              >
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((col) => {
                      const active = sort.by === col.id;
                      return (
                        <TableCell
                          key={col.id}
                          sx={{
                            fontFamily: FONT,
                            fontWeight: 800,
                            fontSize: "13px",
                            letterSpacing: "0.5px",
                            color: active ? "var(--app-accent)" : "#A3A3A3",
                            py: 1.5,
                            whiteSpace: "nowrap",
                            backgroundColor: "var(--app-surface-2)",
                          }}
                        >
                          {col.sortable ? (
                            <TableSortLabel
                              active={active}
                              direction={active ? sort.order : "asc"}
                              onClick={() => handleSort(col.id)}
                              sx={{
                                "&.Mui-active, &.Mui-active .MuiTableSortLabel-icon":
                                  { color: "var(--app-accent)" },
                                "&:hover": { color: "var(--app-accent)" },
                              }}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {COLUMNS.map((col) => (
                            <TableCell key={col.id}>
                              <Skeleton variant="text" width="80%" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : pageRows.map((device, index) => {
                        const status = getWarrantyStatus(device);
                        const activity = getActivity(device);
                        return (
                          <TableRow
                            key={device.id || `${device.serialNumber}-${index}`}
                            sx={{
                              backgroundColor: "var(--app-surface)",
                              "& td": {
                                py: "12px",
                                fontFamily: FONT,
                                fontSize: "14px",
                                color: "var(--app-text)",
                              },
                              "& td:first-of-type": {
                                borderTopLeftRadius: "8px",
                                borderBottomLeftRadius: "8px",
                              },
                              "& td:last-of-type": {
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                              },
                            }}
                          >
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography
                                sx={{
                                  fontFamily: FONT,
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "var(--app-text)",
                                }}
                              >
                                {device.deviceId || "—"}
                              </Typography>
                              {device.serialNumber && (
                                <Typography
                                  sx={{
                                    fontFamily: FONT,
                                    fontSize: "12px",
                                    color: "var(--app-text-muted)",
                                    mt: 0.2,
                                  }}
                                >
                                  SN: {device.serialNumber}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {device.model || "—"}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {formatDate(device.purchaseDate)}
                            </TableCell>

                            <TableCell
                              sx={{
                                whiteSpace: "nowrap",
                                fontWeight: status === "expired" ? 700 : 500,
                                color:
                                  status === "expired"
                                    ? "var(--app-danger) !important"
                                    : "var(--app-text)",
                              }}
                            >
                              {formatDate(device.warrantyExpiry)}
                            </TableCell>

                            <TableCell>
                              <StatusChip status={status} />
                            </TableCell>

                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.8}
                              >
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    backgroundColor: activity.color,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontFamily: FONT,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "var(--app-text)",
                                  }}
                                >
                                  {activity.label}
                                </Typography>
                              </Stack>
                              <Typography
                                sx={{
                                  fontFamily: FONT,
                                  fontSize: "12px",
                                  color: "var(--app-text-muted)",
                                  mt: 0.2,
                                }}
                              >
                                {buildActivityDetail(device)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>

              {!isLoading && !hasMore && filtered.length > PAGE_SIZE && (
                <Box textAlign="center" py={2}>
                  <Typography variant="caption" color="text.secondary">
                    No more records
                  </Typography>
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}
